<template>
  <TextField
    :scope="[...scope, 'attribute_name']"
    v-model:model-value="attribute_name"
    :errors="errors['attribute_name']"
  />
  <SelectField
    :scope="[...scope, 'purpose']"
    :initial-value="purpose"
    :errors="errors['purpose']"
    :options="purposesWithLabels"
  />
  <details class="mb-3 text-muted small">
    <summary>{{ $t('backend.candidacy_config.purposes.what_are') }}</summary>
    <p v-for="purpose in purposes" class="text-muted mt-2 mb-1">
      <strong>{{ $t(`backend.candidacy_config.purposes.${purpose}`) }}:</strong>
      {{ $t(`backend.candidacy_config.purposes.${purpose}_details`) }}
    </p>
  </details>
  <CheckBoxField
    :scope="[...scope, 'required']"
    :initial-value="required"
    :errors="errors['required']"
  />
  <CheckBoxField
    :scope="[...scope, 'editable']"
    :initial-value="editable"
    :errors="errors['editable']"
  />
  <CheckBoxField
    :scope="[...scope, 'public']"
    :initial-value="public"
    :errors="errors['public']"
  />
  <LocaleStringField
    :scope="[...scope, 'label']"
    :locales="locales"
    :initial-value="label"
    :errors="errors['label']"
    :custom-label="getCustomLabel('label')"
  />
  <LocaleStringField
    :scope="[...scope, 'help']"
    :optional="true"
    :locales="locales"
    :initial-value="help"
    :errors="errors['help']"
  />

  <!-- config -->
  <div class="row">
    <!-- Add type specific constraints here -->
    <template v-if="item_type === 'text'">
      <NumberField
        :scope="[...scope, 'minimum_length']"
        :initial-value="initialItem.minimum_length || 0"
        :optional="true"
        :errors="errors['minimum_length']"
      />
      <NumberField
        :scope="[...scope, 'maximum_length']"
        :initial-value="initialItem.maximum_length || 0"
        :optional="true"
        :errors="errors['maximum_length']"
      />
      <CheckBoxField
        :scope="[...scope, 'localised']"
        :initial-value="initialItem.localised || false"
        :errors="errors['localised']"
      />
    </template>
    <template v-else-if="item_type === 'rich_text_area'">
      <NumberField
        :scope="[...scope, 'minimum_length']"
        :initial-value="initialItem.minimum_length || 0"
        :optional="true"
        :errors="errors['minimum_length']"
      />
      <NumberField
        :scope="[...scope, 'maximum_length']"
        :initial-value="initialItem.maximum_length || 0"
        :optional="true"
        :errors="errors['maximum_length']"
      />
      <CheckBoxField
        :scope="[...scope, 'localised']"
        :initial-value="initialItem.localised || false"
        :errors="errors['localised']"
      />
    </template>
    <template v-else-if="item_type === 'date_time'">
      <DateTimeField
        :scope="[...scope, 'earliest']"
        :initial-value="initialItem.earliest ? new Date(initialItem.earliest).toISOString().split('Z')[0] : '' "
        :optional="true"
        :errors="errors['earliest']"
      />
      <DateTimeField
        :scope="[...scope, 'latest']"
        :initial-value="initialItem.latest ? new Date(initialItem.latest).toISOString().split('Z')[0] : '' "
        :optional="true"
        :errors="errors['latest']"
      />
    </template>
    <template v-else-if="item_type === 'date'">
      <DateField
        :scope="[...scope, 'earliest']"
        :initial-value="initialItem.earliest ? new Date(initialItem.earliest).toISOString().split('T')[0] : '' "
        :optional="true"
        :errors="errors['earliest']"
      />
      <DateField
        :scope="[...scope, 'latest']"
        :initial-value="initialItem.latest ? new Date(initialItem.latest).toISOString().split('T')[0] : '' "
        :optional="true"
        :errors="errors['latest']"
      />
    </template>
    <template v-else-if="item_type === 'link_list'">
      <NumberField
        :scope="[...scope, 'minimum_amount']"
        :initial-value="initialItem.minimum_amount || 0"
        :errors="errors['minimum_amount']"
      />
      <NumberField
        :scope="[...scope, 'maximum_amount']"
        :initial-value="initialItem.maximum_amount || 0"
        :errors="errors['maximum_amount']"
      />
    </template>
    <template v-else-if="item_type === 'number'">
      <NumberField
        :scope="[...scope, 'minimum_value']"
        :initial-value="initialItem.minimum_value || 0"
        :errors="errors['minimum_value']"
        :optional="true"
      />
      <NumberField
        :scope="[...scope, 'maximum_value']"
        :initial-value="initialItem.maximum_value || 0"
        :errors="errors['maximum_value']"
        :optional="true"
      />
    </template>
    <template v-else-if="item_type === 'select'">
      <Select
        :scope="[...scope, 'selectables']"
        v-model:model-value="selectables"
        :errors="errors['selectables']"
        :enabled-locales="locales"
      />
    </template>
  </div>
</template>

<script lang="ts">
import NumberField from "./shared/NumberField.vue";
import LocaleStringField from "./shared/LocaleStringField.vue";
import CheckBoxField from "./shared/CheckBoxField.vue";
import TextField from "./shared/TextField.vue";
import Select from "./shared/Select.vue";
import { defineComponent } from "vue";
import { humanize } from "@/lib/candidacy/form_builder/utils";
import type { EveryBuildableItem, FormItemPurpose, Locale, PropType, LocalString, SelectableOptionItem, BuildableItemErrors } from "@/types";
import SelectField from "./shared/SelectField.vue";
import DateTimeField from '@/components/backend/candidacy/form_builder/form_items/shared/DateTimeField.vue';
import DateField from '@/components/backend/candidacy/form_builder/form_items/shared/DateField.vue';

export default defineComponent({
  name: "FieldItem",
  components: { DateField, DateTimeField, TextField, CheckBoxField, LocaleStringField, NumberField, SelectField, Select},
  props: {
    initialItem: {
      type: Object as PropType<EveryBuildableItem>,
      required: true,
    },
    scope: {
      type: Array as PropType<string[]>,
      default: []
    },
    locales: {
      type: Array as PropType<Locale[]>,
      default: ["en"],
    },
    purposes: {
      type: Array as PropType<FormItemPurpose[]>,
      required: true
    }
  },
  data: ({ initialItem }) => ({
    id: initialItem.id,
    type: initialItem.type,
    item_type: initialItem.item_type,
    attribute_name: initialItem.attribute_name || "",
    required: initialItem.required ?? false,
    editable: initialItem.editable ?? true,
    public: initialItem.public ?? true,
    config: initialItem.config || {},
    earliest: initialItem.earliest || "",
    latest: initialItem.latest || "",
    label: initialItem.label || {} satisfies LocalString,
    help: initialItem.help || {} satisfies LocalString,
    errors: initialItem.errors || {} satisfies BuildableItemErrors,
    purpose: initialItem.purpose || "generic" satisfies FormItemPurpose,
    selectables: initialItem.selectables || [] satisfies SelectableOptionItem[],
  }),
  methods: {
    humanize,
    getCustomLabel(attribute: string) {
      const key = `activerecord.attributes.candidacy/form_item.${attribute}`;
      return this.label || this.$te(key) ? this.$t(key) : humanize(attribute);
    },
  },
  computed: {
    filteredPurposes() {
      switch (this.initialItem.item_type) {
        case "text": return this.purposes;
        case "select": return this.purposes;
        case "rich_text_area": return ["generic"];
        default: return ["generic", "summary"];
      };
    },
    purposesWithLabels() {
      return Object.fromEntries(this.filteredPurposes.map((purpose: string) => [purpose, this.$t(`backend.candidacy_config.purposes.${purpose}`)]));
    }
  },
});
</script>
